import React from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import { Box } from '@mui/material';
import { VALIDATION_MOBILE_PHONE } from '@uplift/validations';
import { useIsMobile } from '@uplift/hooks';
import { ActionItemKeys, ContactName, RewriteUserContactByNameErrorResponse } from '@uplift/api';
import { CancelButton, SubmitButton } from '../../../Form';
import { ModalContent, ModalActions, ModalTitle } from '../../../Modal';
import { MaskedInput } from '../../../Inputs';
import { getFormErrorsFromRewriteUserContactByNameResponse } from '@uplift/utils';
import { AxiosError } from 'axios';
import { useUpdateMobileNumber } from './PhoneVerificationModal.hooks';

export type EditPhoneFormProps = {
  open: boolean;
  userId: string;
  phone: string;
  contactName: ContactName;
  onClose?: () => void;
};

type PhoneForm = {
  phone: string;
};

const validationSchema = Yup.object().shape({
  phone: VALIDATION_MOBILE_PHONE,
});


export const EditPhoneForm: React.FC<EditPhoneFormProps> = (props) => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { updateMobileNumber } = useUpdateMobileNumber();

  const initialValues: PhoneForm = {
    phone: props.phone ?? '',
  };

  const onSubmit = async (values: PhoneForm, formikHelpers: FormikHelpers<PhoneForm>) => {
    try {
      await updateMobileNumber({ userId: props.userId, name: props.contactName, number: values.phone });
      await Promise.all([
        queryClient.invalidateQueries([ 'user-contacts', ContactName.mobile ]),
        queryClient.invalidateQueries([ 'user', props.userId, 'contacts' ]),
        queryClient.invalidateQueries([ 'actionItem', ActionItemKeys.CLIENT_SMS_VERIFY ]),
      ]);
    } catch(error) {
      const errorObj = getFormErrorsFromRewriteUserContactByNameResponse(error as AxiosError<RewriteUserContactByNameErrorResponse>, props.contactName);

      if (errorObj && errorObj[props.contactName]) {
        formikHelpers.setErrors({ phone: errorObj[props.contactName] });
        return;
      }
    }
    props.onClose?.();
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
      validationSchema={validationSchema}
    >
      <>
        <ModalTitle onClose={props.onClose}>Edit Mobile Number</ModalTitle>
        <ModalContent>
          <MaskedInput label="Mobile" autoFocus name="phone" type="phone" />
          <Box mt={15} />
        </ModalContent>
        <ModalActions>
          <Box width="100%" display="flex" justifyContent="space-between" gap="10px">
            <CancelButton fullWidth={isMobile} onClick={props.onClose} variant={isMobile ? 'outlined' : undefined}>Cancel</CancelButton>
            <SubmitButton fullWidth={isMobile} color="secondary">Update</SubmitButton>
          </Box>
        </ModalActions>
      </>
    </Formik>
  );
};
